import {
    CommentItem,
    ExpenseKindEnum,
    ExpenseRequestTypeEnum,
    TransactionData,
    InboxInvoice,
    Expense,
    SplitTypeEnum,
    InvoiceSplit,
    Employee,
    Hospitality,
    Mileage,
    PerDiem,
    VendorTypeEnum,
} from "@finway-group/shared/lib/models"
import { AutoGeneratedCommentsEnum } from "@finway-group/shared/lib/models/comment/autoGeneratedComments.enum"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { Button, Form, Modal } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import ExpenseForm from "Components/forms/expenseForm/expenseForm"
import { NOT_SET_VALUE } from "Features/pages/expenses/expenseDetails/expenseSplitCells/cell.utils"
import {
    addInvoiceCommentsIfNeeded,
    addUpdateCommentToExpense,
    checkForDuplicate,
    deleteUnnecessaryExpenseFormData,
    doTotalAndInlineAmountsMismatch,
    isMileageExpense,
    isMileageOrPerDiem,
    isPerDiemExpense,
    syncTotalExpenseAmountsToItems,
} from "Features/pages/expenses/expenseUtils"
import { DEFAULT_PER_DIEM_DESTINATION, FINWAY_ADMIN_EMAIL, REIMBURSABLE_DURATION_THRESHOLD_MINUTE } from "Shared/config/consts"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { NotificationService, UserService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { createExpense, createPreApprovedSubscription, updateExpense } from "Shared/store/actions/expense/expenseActions"
import { deleteManyInboxInvoices } from "Shared/store/actions/inboxInvoice/inboxInvoiceActions"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import ExpenseFormContext from "./expenseCreateForm.context"
import { useVendors } from "Shared/hooks/vendor.hooks"
import { useCardById } from "Shared/hooks/creditCard.hooks"
import { useExpense } from "Shared/hooks/expense.hooks"
import DialogService from "Shared/services/dialog.service"
import { shouldAdjustLimit } from "Features/pages/creditCards/creditCard.utils"
import { getAbsentApprovers, getAvailableReplacementsForAbsentApprover } from "Features/pages/employees/employeeUtils"
import { useWorkflows } from "Shared/hooks/workflow.hooks"
import { ExpensePaymentFlowInformationEnum } from "@finway-group/shared/lib/models/expense/expensePaymentFlowInformation.enum"
import ReplacementForAbsentApproverModal from "./replacementForAbsentApprover.modal"
import { useCompany } from "Shared/hooks/company.hooks"
import { isPerDiemTripNotReimbursable } from "Components/forms/expenseForm/reimbursementForms/perDiem/helpers"
import { usePerDiemDestinations } from "Shared/hooks/perDiemDestination.hooks"

interface ExpenseCreateFormInterface {
    kind?: ExpenseKindEnum
    type: ExpenseRequestTypeEnum
    hideExpenseKindRadioOption?: boolean
    isShowing: boolean
    isNew: boolean
    isPreApprovedSubscription?: boolean
    transactionData?: TransactionData
    isReimbursement?: boolean
    onExpenseCreated?: (expense: Expense) => void
    onCancel: () => void
    inboxInvoices?: InboxInvoice[]
    inboxOcrSource?: InboxInvoice

    // Enables/Disables request types selection. By default all are enabled.
    reimbursementRequestsEnabled?: { standard: boolean; mileage: boolean; hospitality: boolean; perDiem: boolean }
}
const ExpenseCreateFormModal: React.FC<ExpenseCreateFormInterface> = ({
    type,
    kind = ExpenseKindEnum.ONE_TIME_EXPENSE,
    isShowing,
    isNew = true,
    isPreApprovedSubscription = false,
    isReimbursement = false,
    hideExpenseKindRadioOption,
    transactionData,
    onExpenseCreated,
    onCancel,
    inboxInvoices,
    inboxOcrSource,
    reimbursementRequestsEnabled,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const employees = useEmployees(true, false, true)
    const workflows = useWorkflows()
    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)
    const [isUploading, setIsUploading] = useStateIfMounted(false)
    const [isReplacementForAbsentApproverShowing, setIsReplacementForAbsentApproverShowing] = useStateIfMounted(false)
    const [absentApprovers, setAbsentApprovers] = useStateIfMounted<Employee[]>([])
    const [availableReplacementsForAbsentApprover, setAvailableReplacementsForAbsentApprover] = useStateIfMounted<Employee[]>([])
    const [checkIfApproverInAbsence, setCheckIfApproverInAbsence] = useStateIfMounted(true)
    const [replacementForAbsentApprover, setReplacementForAbsentApprover] = useStateIfMounted("")
    const perDiemDestinations = usePerDiemDestinations()

    const [expenseForm] = Form.useForm()
    const taxes = useTaxes()
    const vendors = useVendors()
    const expenseDetails = useExpense()
    const company = useCompany()
    const { card } = useCardById(expenseDetails.card)

    const isInvoiceRequest = type === ExpenseRequestTypeEnum.INVOICE
    const isPurchaseRequest = type === ExpenseRequestTypeEnum.PURCHASE
    isReimbursement = type === ExpenseRequestTypeEnum.REIMBURSEMENT

    const onSubmit = async (expense: (Expense & { replacementForAbsentApprover?: string }) | Mileage | Hospitality | PerDiem, extraData?: { originalApproverId: string }) => {
        setIsLoading(true)
        if (checkIfApproverInAbsence && !replacementForAbsentApprover) {
            const absentApprovers = getAbsentApprovers(expense, workflows, employees)
            if (absentApprovers.length > 0) {
                setAvailableReplacementsForAbsentApprover(getAvailableReplacementsForAbsentApprover(expense, employees))
                setAbsentApprovers(absentApprovers)
                setIsReplacementForAbsentApproverShowing(true)
                setIsLoading(false)
                return
            }
        }
        const autoApprover = employees.find(({ email }) => email === FINWAY_ADMIN_EMAIL)

        ;(expense as any).replacementForAbsentApprover = replacementForAbsentApprover

        // check for duplicate
        if (!(await checkForDuplicate(expense, expense.invoiceNumber, { ignoreIds: inboxInvoices?.map(({ id }) => id) }))) {
            setIsLoading(false)
            return
        }

        // confirm reporting only status
        if (
            expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY &&
            !(await DialogService.confirmPaymentFlowInformationReportingOnlyWarningOnCreation())
        ) {
            setIsLoading(false)
            return
        }

        // confirm mismatching amounts
        if (doTotalAndInlineAmountsMismatch(expense, taxes, isNew) && (await DialogService.confirmAmountAdjustment())) {
            const { totalNetPrice, totalTaxPrice, totalGrossPrice, taxRate } = syncTotalExpenseAmountsToItems(expense, taxes)
            expense = new Expense({ ...expense, totalNetPrice, totalTaxPrice, totalGrossPrice, taxRate })
        }

        if (isPerDiemExpense(expense)) {
            if (isPerDiemTripNotReimbursable(expense, perDiemDestinations)) {
                await DialogService.confirmPerDiemLessThan8Hours()
                setIsLoading(false)
                return
            }
        }

        // if cost center 2 or expense account is "not set" remove value because api "" (empty string) is not a valid ID
        // and undefined select value is not supported by antd :/
        if (expense.costCenter2 === NOT_SET_VALUE) expense.costCenter2 = undefined
        if (expense.expenseAccount === NOT_SET_VALUE) expense.expenseAccount = undefined

        // Clean split's cc2 or expense account from NOT_SET_VALUE
        for (const split of expense.splits) {
            if (split.costCenter2 === NOT_SET_VALUE) split.costCenter2 = undefined
            if (split.expenseAccount === NOT_SET_VALUE) split.expenseAccount = undefined as any
        }

        // check for attached card limit update
        if (!isNew && loggedInUser.activeCompanyProfile.isAdmin && shouldAdjustLimit(expenseDetails, expense, card))
            expense.shouldUpdateCardLimit = await DialogService.confirmCardLimitUpdate()

        // confirm auto approver
        if (
            expense.approvalNeededBy === autoApprover?.id &&
            company?.showAutoApproverWarning &&
            (extraData?.originalApproverId !== autoApprover.id || extraData?.originalApproverId !== expense.approvalNeededBy || isNew) &&
            expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY &&
            !(await DialogService.confirmAutoApprover())
        ) {
            setIsLoading(false)
            return
        }

        if (expense.splitType === SplitTypeEnum.SPLIT && expense.splits.length === 1) {
            const split = expense.splits[0] as InvoiceSplit
            split.taxRate = expense.taxRate
            split.netPrice = expense.totalNetPrice
            split.grossPrice = expense.totalGrossPrice
            split.taxPrice = expense.totalTaxPrice
        }

        if (isMileageOrPerDiem(expense)) {
            expense.vendor = expense.requestedBy
            expense.vendorType = VendorTypeEnum.USER
            // For mileage requests, the invoiceDate will be datePurchased.
            // For per diem requests, the invoiceDate will be datePurchased, derived from the last date of the trip.
            expense.invoiceDate = expense.datePurchased
        }

        onExpenseAction(expense)
    }

    const onExpenseAction = (expense: Expense | Mileage | Hospitality | PerDiem) => {
        setIsLoading(true)
        // delete helper form fields
        deleteUnnecessaryExpenseFormData(expense)
        if (isNew) onCreateExpense(expense)
        else onUpdateExpense(expense)
    }

    const onCreateExpense = async (expense: Expense | Mileage | Hospitality | PerDiem) => {
        if (!expense.expenseAccount) {
            const vendor = vendors.find((v) => v.id === expense.vendor)
            expense.expenseAccount = vendor?.rule?.expenseAccount
        }

        const createAction = isPreApprovedSubscription ? createPreApprovedSubscription : createExpense

        createAction(expense)(dispatch)
            .then((newExpense: Expense) => {
                if (
                    expense.isSubscription() &&
                    expense.isSuccessfullyProcessed() &&
                    expense.isInitialSubscription() &&
                    expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD
                ) {
                    NotificationService.send(
                        NotificationTypeEnum.SUCCESS,
                        t("notification:request.created_with_card_processing.title"),
                        t("notification:request.created_with_card_processing.message"),
                        20
                    )
                } else {
                    NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:request.created.title"), t("notification:request.created.message"))
                }

                if (onExpenseCreated) onExpenseCreated(newExpense)

                if (inboxInvoices?.length) deleteManyInboxInvoices(inboxInvoices.map((invoice) => invoice.id))(dispatch)
                setReplacementForAbsentApprover("")
                setCheckIfApproverInAbsence(true)
                onHide()
            })
            .catch((err) => {
                setIsLoading(false)
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:request.create.title"))
            })
    }

    const onUpdateExpense = async (expense: Expense) => {
        addInvoiceCommentsIfNeeded(expense, expenseDetails, loggedInUser)

        // prettier-ignore
        updateExpense(expense.id, expense)(dispatch)
            .then(() => {
                NotificationService.send(NotificationTypeEnum.SUCCESS, t('notification:request.updated.title'), t('notification:request.updated.message'));

                if (inboxInvoices?.length) deleteManyInboxInvoices(inboxInvoices.map((invoice) => invoice.id))(dispatch)
                setReplacementForAbsentApprover("")
                setCheckIfApproverInAbsence(true)
                onHide();
            })
            .catch((err) => {
                setIsLoading(false)
                NotificationService.showErrorNotificationBasedOnResponseError(err, t('error:request.edit.title'));
            });
    }

    const getModalTitle = (): string => {
        if (isPreApprovedSubscription) {
            return isNew ? t("action:subscription.create") : t("action:subscription.edit")
        }

        if (isReimbursement) {
            if (isNew) return t("action:reimbursement_request.create")
            else if (isMileageExpense(expenseDetails) || isPerDiemExpense(expenseDetails)) return t("action:reimbursement_request.edit")
            else return t("action:invoice_request.edit")
        }

        if (isInvoiceRequest) {
            return isNew ? t("action:invoice_request.create") : t("action:invoice_request.edit")
        }

        if (isPurchaseRequest) {
            return isNew ? t("action:purchase_request.create") : t("action:purchase_request.edit")
        }

        return isNew ? t("action:request.create") : t("action:request.edit")
    }

    const getActionButtonTitle = () =>
        isPreApprovedSubscription ? (isNew ? t("action:subscription.submit") : t("action:subscription.save")) : isNew ? t("action:request.submit") : t("action:request.save")

    const onHide = () => {
        setIsLoading(false)
        expenseForm.resetFields()
        onCancel()
    }

    return (
        <Modal
            destroyOnClose={true}
            afterClose={() => expenseForm.resetFields()}
            visible={isShowing}
            maskClosable={false}
            title={getModalTitle()}
            onCancel={onCancel}
            closable={true}
            keyboard={true}
            className={isInvoiceRequest || isReimbursement ? "ant-modal--full" : "ant-modal--large"}
            footer={[
                <Button className="btn-default" key="back" onClick={onHide}>
                    {t("action:cancel")}
                </Button>,
                <Button key="submit" type="primary" loading={isLoading} disabled={isUploading} onClick={() => expenseForm.submit()}>
                    {getActionButtonTitle()}
                </Button>,
            ]}
        >
            <ExpenseFormContext
                isNew={isNew}
                type={type}
                expenseForm={expenseForm}
                kind={kind}
                isReimbursement={isReimbursement}
                isPreApprovedSubscription={isPreApprovedSubscription}
                hideExpenseKindRadioOption={!isNew || !!hideExpenseKindRadioOption}
                transactionData={transactionData}
                isUploading={isUploading}
                setIsUploading={setIsUploading}
                inboxInvoices={inboxInvoices}
                reimbursementRequestsEnabled={reimbursementRequestsEnabled}
            >
                <ExpenseForm inboxOcrSource={inboxOcrSource} onSubmit={onSubmit} />
            </ExpenseFormContext>
            {isReplacementForAbsentApproverShowing && (
                <ReplacementForAbsentApproverModal
                    availableReplacementsForAbsentApprover={availableReplacementsForAbsentApprover}
                    absentApprovers={absentApprovers}
                    isVisible={isReplacementForAbsentApproverShowing}
                    closeModal={() => setIsReplacementForAbsentApproverShowing(false)}
                    onSubmit={() => expenseForm.submit()}
                    setCheckIfApproverInAbsence={setCheckIfApproverInAbsence}
                    setReplacementForAbsentApprover={setReplacementForAbsentApprover}
                    isPurchaseRequest={isPurchaseRequest}
                />
            )}
        </Modal>
    )
}

export default ExpenseCreateFormModal
